import { toast, EToastType } from '@bfe/moly';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { addListener } from './wallet-hub/misc';

export const usePopoverState = (initialVisible = false, initialMount?: boolean, keepVisible = false) => {
  const [visible, setVisible] = useState(initialVisible);
  const [mountContent, setMountContent] = useState(visible ? visible : initialMount ?? visible);
  const domVisible = mountContent || visible;

  const show = useCallback(() => {
    setMountContent(true);
    setTimeout(setVisible, 0, true);
  }, []);
  const hide = useCallback(() => setVisible(false), []);
  const destroy = useCallback(() => setMountContent(false), []);
  const hideAndDestroy = useCallback(() => {
    setVisible(false);
    setMountContent(false);
  }, []);

  const transitionEnd = () => {
    if (!visible) {
      destroy();
    }
  };

  return {
    visible: visible || keepVisible,
    domVisible: domVisible || keepVisible,
    show,
    hide,
    destroy,
    hideAndDestroy,
    transitionEnd,
  };
};

export const useToastFn = () => {
  const makeFn = (type: any) => {
    return (title: ReactNode, d?: number) => {
      return toast({
        title,
        iconType: type,
        duration: d,
      });
    };
  };
  return {
    success: makeFn(EToastType.SUCCESS),
    info: makeFn(EToastType.INFO),
    fail: makeFn(EToastType.FAIL),
    process: makeFn(EToastType.PROCESS),
    warn: makeFn(EToastType.WARN),
  };
};

export const useEventUpdate = <T>(obj: any, event: string, getter: () => T) => {
  const state = getter();
  const [, set] = useState(0);
  useEffect(() => {
    return addListener(obj, event, () => set((n) => n + 1));
  }, [obj, event]);
  return state;
};

export const useMemoizedFn = <T extends (...args: any[]) => any>(fn: T) => {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  return useCallback((...args: Parameters<T>) => {
    return fnRef.current(...args);
  }, []);
};
